import React, { useState, useEffect } from "react";
import { ArrowLeft } from "lucide-react";
import { Tabs, Select, Table, Modal, message, Input, DatePicker } from "antd";
import ExcelJS from "exceljs";
import {
  getBirthdayReport,
  getNewAppointmentsReport,
  getResignationReport,
  getMaternityLeaveReport,
  getHrAuditData,
} from "../../service/employeeReportService";

const { TabPane } = Tabs;
const { Option } = Select;
const { RangePicker } = DatePicker;

const EmployeeReports = () => {
  const [activeTab, setActiveTab] = useState("birthdays");
  const [dataSource, setDataSource] = useState({
    birthdays: [],
    newAppointments: [],
    resignations: [],
    maternityLeaves: [],
  });
  const [filters, setFilters] = useState({
    epf_number: "",
    branch: null,
    center: null,
    month: null,
    year: null,
    start_date: null,
    end_date: null,
  });

  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 5,
    total: 0,
  });
  const [pageSizeModalVisible, setPageSizeModalVisible] = useState(false);
  const [tempPageSize, setTempPageSize] = useState(pagination.pageSize);
  const [loading, setLoading] = useState(false);

  const branchOptions = [
    { value: "wattala", label: "Wattala" },
    { value: "negombo", label: "Negombo" },
    { value: "kaduwela", label: "Kaduwela" },
    { value: "kiribathgoda", label: "Kiribathgoda" },
    { value: "kandana", label: "Kandana" },
    { value: "delgoda", label: "Delgoda" },
    { value: "lsu", label: "Wattala LSU" },
    { value: "cityschool", label: "City School - Negombo" }
  ];

  const centerOptions = [
    { value: "OKI", label: "OKI" },
    { value: "OKIDS", label: "OKIDS" },
    { value: "OKIT", label: "OKIT" },
  ];

  const monthOptions = [
    { value: null, label: "All Months" },
    { value: "01", label: "January" },
    { value: "02", label: "February" },
    { value: "03", label: "March" },
    { value: "04", label: "April" },
    { value: "05", label: "May" },
    { value: "06", label: "June" },
    { value: "07", label: "July" },
    { value: "08", label: "August" },
    { value: "09", label: "September" },
    { value: "10", label: "October" },
    { value: "11", label: "November" },
    { value: "12", label: "December" },
  ];

  const yearOptions = [
    { value: null, label: "All Years" },
    ...Array.from({ length: 2030 - 1950 + 1 }, (_, i) => ({
      value: (1950 + i).toString(),
      label: (1950 + i).toString(),
    })),
  ];

  const handleFilterChange = (key, value) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
    setPagination((prev) => ({ ...prev, current: 1 }));
  };

  const handleDateRangeChange = (dates) => {
    setFilters((prev) => ({
      ...prev,
      start_date: dates ? dates[0]?.format("YYYY-MM-DD") : null,
      end_date: dates ? dates[1]?.format("YYYY-MM-DD") : null,
    }));
    setPagination((prev) => ({ ...prev, current: 1 }));
  };

  const handleTableChange = (newPagination) => {
    setPagination(newPagination);
  };

  const handlePageSizeChange = () => {
    setPagination((prev) => ({ ...prev, pageSize: tempPageSize, current: 1 }));
    setPageSizeModalVisible(false);
  };

  const fetchReportData = async (tabKey) => {
    try {
      setLoading(true);
      const params = {
        epf_number: filters.epf_number || undefined,
        branch: filters.branch || undefined,
        center: filters.center || undefined,
      };

      if (tabKey === "birthdays") {
        params.month = filters.month || undefined;
        params.year = filters.year || undefined;
      } else {
        params.start_date = filters.start_date || undefined;
        params.end_date = filters.end_date || undefined;
      }

      let response;
      switch (tabKey) {
        case "birthdays":
          response = await getBirthdayReport(params);
          break;
        case "newAppointments":
          response = await getNewAppointmentsReport(params);
          break;
        case "resignations":
          response = await getResignationReport(params);
          break;
        case "maternityLeaves":
          response = await getMaternityLeaveReport(params);
          break;
        default:
          throw new Error("Invalid tab key");
      }

      const mappedData = Array.isArray(response.data)
        ? response.data
            .filter((item) => item.name && item.name !== "-")
            .map((item) => ({
              id: item.employee_id,
              epf_number: item.epf_number || "-",
              name: item.name || "-",
              branch: item.branch || "-",
              center: item.center || "-",
              employee_type: item.employee_type || "-",
              date_of_birth: item.date_of_birth || "-",
              joining_date: item.joining_date || "-",
              resign_date: item.resign_date || "-",
              start_date: item.start_date || "-",
              end_date: item.end_date || "-",
              expected_delivery_date: item.expected_delivery_date || "-",
              total_days: item.total_days || 0,
            }))
        : [];

      setDataSource((prev) => ({ ...prev, [tabKey]: mappedData }));
      setPagination((prev) => ({
        ...prev,
        total: mappedData.length,
        current: 1,
      }));
      setLoading(false);
      if (mappedData.length === 0) {
        message.warning(`No ${tabKey} records found for the selected filters.`);
      }
    } catch (error) {
      console.error(`Error fetching ${tabKey} report:`, error);
      setLoading(false);
      message.error(`Failed to fetch ${tabKey} report. Please try again.`);
    }
  };

  useEffect(() => {
    fetchReportData(activeTab);
  }, [filters, activeTab]);

  const exportHrAudit = async () => {
    try {
      setLoading(true);
      const res = await getHrAuditData();
      const employees = res.data || [];
      const workbook = new ExcelJS.Workbook();
      const ws = workbook.addWorksheet("HR Audit");
      const borderStyle = { top:{style:"thin"}, left:{style:"thin"}, bottom:{style:"thin"}, right:{style:"thin"} };
      ws.columns = [
        { header: "EPF No",    key: "epf_number",      width: 12 },
        { header: "Name",      key: "full_name",        width: 35 },
        { header: "NIC",       key: "nic",              width: 16 },
        { header: "Join Date", key: "joining_date",     width: 14 },
        { header: "Branch",    key: "branch",           width: 18 },
        { header: "Center",    key: "center",           width: 12 },
        { header: "Emp Type",  key: "employee_type",    width: 14 },
        { header: "Category",  key: "employeeCategory", width: 14 },
      ];
      const hdr = ws.getRow(1);
      hdr.font = { bold: true };
      hdr.fill = { type: "pattern", pattern: "solid", fgColor: { argb: "FFD3D3D3" } };
      hdr.alignment = { vertical: "middle", horizontal: "center" };
      hdr.eachCell(c => { c.border = borderStyle; });
      employees.forEach(emp => {
        const row = ws.addRow({
          epf_number: emp.epf_number || "-",
          full_name: emp.full_name || `${emp.first_name || ""} ${emp.last_name || ""}`.trim() || "-",
          nic: emp.nic || "-",
          joining_date: emp.joining_date || "-",
          branch: emp.branch || "-",
          center: emp.center || "-",
          employee_type: emp.employee_type || "-",
          employeeCategory: emp.employeeCategory || "-",
        });
        row.eachCell(c => { c.border = borderStyle; c.alignment = { vertical: "middle", horizontal: "center" }; });
      });
      const buf = await workbook.xlsx.writeBuffer();
      const url = URL.createObjectURL(new Blob([buf], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" }));
      Object.assign(document.createElement("a"), { href: url, download: `hr_audit_${new Date().toISOString().slice(0,10)}.xlsx` }).click();
      URL.revokeObjectURL(url);
      message.success(`Exported ${employees.length} employees to Excel.`);
    } catch (e) {
      message.error("Failed to export HR Audit. " + e.message);
    } finally {
      setLoading(false);
    }
  };

  const handleExcelExport = async (reportType) => {
    try {
      setLoading(true);
      const workbook = new ExcelJS.Workbook();
      const worksheet = workbook.addWorksheet(`${reportType} Report`);

      worksheet.properties.defaultRowHeight = 40;
      worksheet.properties.defaultColWidth = 10;
      worksheet.views = [{ state: "frozen", ySplit: 1 }];

      const borderStyle = {
        top: { style: "thin", color: { argb: "FF000000" } },
        left: { style: "thin", color: { argb: "FF000000" } },
        bottom: { style: "thin", color: { argb: "FF000000" } },
        right: { style: "thin", color: { argb: "FF000000" } },
      };

      const headerStyle = {
        font: { bold: true, size: 10, color: { argb: "FF000000" } },
        fill: {
          type: "pattern",
          pattern: "solid",
          fgColor: { argb: "FFD3D3D3" },
        },
        alignment: {
          vertical: "middle",
          horizontal: "center",
          textRotation: 90,
          wrapText: true,
        },
        border: borderStyle,
      };

      let columns, data;
      switch (reportType) {
        case "Birthdays":
          columns = [
            { header: "EPF Number", key: "epf_number", width: 12 },
            { header: "Name", key: "name", width: 35 },
            { header: "Date of Birth", key: "date_of_birth", width: 15 },
            { header: "Branch", key: "branch", width: 20 },
            { header: "Center", key: "center", width: 15 },
            { header: "Employee Type", key: "employee_type", width: 15 },
          ];
          data = dataSource.birthdays;
          break;
        case "New Appointments":
          columns = [
            { header: "EPF Number", key: "epf_number", width: 12 },
            { header: "Name", key: "name", width: 35 },
            { header: "Joining Date", key: "joining_date", width: 15 },
            { header: "Branch", key: "branch", width: 20 },
            { header: "Center", key: "center", width: 15 },
            { header: "Employee Type", key: "employee_type", width: 15 },
          ];
          data = dataSource.newAppointments;
          break;
        case "Resignations":
          columns = [
            { header: "EPF Number", key: "epf_number", width: 12 },
            { header: "Name", key: "name", width: 35 },
            { header: "Resignation Date", key: "resign_date", width: 15 },
            { header: "Branch", key: "branch", width: 20 },
            { header: "Center", key: "center", width: 15 },
            { header: "Employee Type", key: "employee_type", width: 15 },
          ];
          data = dataSource.resignations;
          break;
        case "Maternity Leaves":
          columns = [
            { header: "EPF Number", key: "epf_number", width: 12 },
            { header: "Name", key: "name", width: 35 },
            { header: "Start Date", key: "start_date", width: 15 },
            { header: "End Date", key: "end_date", width: 15 },
            { header: "Expected Delivery", key: "expected_delivery_date", width: 15 },
            { header: "Total Days", key: "total_days", width: 12 },
            { header: "Branch", key: "branch", width: 20 },
            { header: "Center", key: "center", width: 15 },
            { header: "Employee Type", key: "employee_type", width: 15 },
          ];
          data = dataSource.maternityLeaves;
          break;
        default:
          throw new Error("Invalid report type");
      }

      worksheet.columns = columns;

      const headerRow = worksheet.getRow(1);
      headerRow.height = 80;
      headerRow.eachCell((cell) => {
        cell.style = { ...headerStyle };
        cell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: "FFFFFFFF" } };
      });

      data.forEach((item) => {
        const row = worksheet.addRow(item);
        row.eachCell((cell) => {
          cell.border = borderStyle;
          cell.alignment = { vertical: "middle", horizontal: "center", wrapText: true };
        });
      });

      worksheet.addRow([]);
      worksheet.addRow(["OKI International", ...new Array(columns.length - 1).fill("")]);
      worksheet.addRow([`${reportType} Report`, ...new Array(columns.length - 1).fill("")]);
      if (reportType === "Birthdays") {
        if (filters.month || filters.year) {
          const monthLabel = monthOptions.find((opt) => opt.value === filters.month)?.label || "Any";
          const yearLabel = filters.year || "Any";
          const dateFilter = `Date: ${monthLabel}/${yearLabel}`;
          worksheet.addRow([dateFilter, ...new Array(columns.length - 1).fill("")]);
          worksheet.mergeCells(`A${data.length + 4}:${String.fromCharCode(64 + columns.length)}${data.length + 4}`);
        }
      } else if (filters.start_date || filters.end_date) {
        const dateFilter = `Date Range: ${filters.start_date || "Any"} to ${filters.end_date || "Any"}`;
        worksheet.addRow([dateFilter, ...new Array(columns.length - 1).fill("")]);
        worksheet.mergeCells(`A${data.length + 4}:${String.fromCharCode(64 + columns.length)}${data.length + 4}`);
      }
      worksheet.mergeCells(`A${data.length + 2}:${String.fromCharCode(64 + columns.length)}${data.length + 2}`);
      worksheet.mergeCells(`A${data.length + 3}:${String.fromCharCode(64 + columns.length)}${data.length + 3}`);

      const buffer = await workbook.xlsx.writeBuffer();
      const blob = new Blob([buffer], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${reportType.toLowerCase().replace(" ", "_")}_report.xlsx`;
      a.click();
      window.URL.revokeObjectURL(url);
      setLoading(false);
    } catch (error) {
      setLoading(false);
      console.error(`Error exporting ${reportType} to Excel:`, error);
      message.error(`Failed to export ${reportType} report to Excel. Please try again.`);
    }
  };

  const columns = {
    birthdays: [
      { title: "EPF No", dataIndex: "epf_number", render: (text) => text || "-" },
      { title: "Name", dataIndex: "name", render: (text) => text || "-" },
      { title: "Date of Birth", dataIndex: "date_of_birth", render: (text) => text || "-" },
      { title: "Branch", dataIndex: "branch", render: (text) => text || "-" },
      { title: "Center", dataIndex: "center", render: (text) => text || "-" },
      { title: "Employee Type", dataIndex: "employee_type", render: (text) => text || "-" },
    ],
    newAppointments: [
      { title: "EPF No", dataIndex: "epf_number", render: (text) => text || "-" },
      { title: "Name", dataIndex: "name", render: (text) => text || "-" },
      { title: "Joining Date", dataIndex: "joining_date", render: (text) => text || "-" },
      { title: "Branch", dataIndex: "branch", render: (text) => text || "-" },
      { title: "Center", dataIndex: "center", render: (text) => text || "-" },
      { title: "Employee Type", dataIndex: "employee_type", render: (text) => text || "-" },
    ],
    resignations: [
      { title: "EPF No", dataIndex: "epf_number", render: (text) => text || "-" },
      { title: "Name", dataIndex: "name", render: (text) => text || "-" },
      { title: "Resignation Date", dataIndex: "resign_date", render: (text) => text || "-" },
      { title: "Branch", dataIndex: "branch", render: (text) => text || "-" },
      { title: "Center", dataIndex: "center", render: (text) => text || "-" },
      { title: "Employee Type", dataIndex: "employee_type", render: (text) => text || "-" },
    ],
    maternityLeaves: [
      { title: "EPF No", dataIndex: "epf_number", render: (text) => text || "-" },
      { title: "Name", dataIndex: "name", render: (text) => text || "-" },
      { title: "Start Date", dataIndex: "start_date", render: (text) => text || "-" },
      { title: "End Date", dataIndex: "end_date", render: (text) => text || "-" },
      { title: "Expected Delivery", dataIndex: "expected_delivery_date", render: (text) => text || "-" },
      { title: "Total Days", dataIndex: "total_days", render: (text) => text || 0 },
      { title: "Branch", dataIndex: "branch", render: (text) => text || "-" },
      { title: "Center", dataIndex: "center", render: (text) => text || "-" },
      { title: "Employee Type", dataIndex: "employee_type", render: (text) => text || "-" },
    ],
  };

  return (
    <div className="page-wrapper-lt">
      <div className="content">
        <div className="page-header">
          <div className="add-item d-flex">
            <div className="header-leave-container">
              <div>
                <ArrowLeft size={32} />
              </div>
              <div className="page-title">
                <h4>Employee Reports</h4>
                <h6></h6>
              </div>
            </div>
            <div className="wordset">
              <ul>
                <li>
                  <a
                    title="HR Audit Export"
                    onClick={exportHrAudit}
                    style={{ cursor: "pointer", marginRight: 8 }}
                  >
                    <span style={{ fontSize: 12, fontWeight: 600, color: "#00c5fb" }}>HR Audit</span>
                  </a>
                </li>
                <li>
                  <a
                    title="excel"
                    onClick={() =>
                      handleExcelExport(
                        activeTab === "birthdays"
                          ? "Birthdays"
                          : activeTab === "newAppointments"
                          ? "New Appointments"
                          : activeTab === "resignations"
                          ? "Resignations"
                          : "Maternity Leaves"
                      )
                    }
                  >
                    <img src="assets/img/icons/excel.svg" alt="excel" />
                  </a>
                </li>
              </ul>
            </div>
          </div>
        </div>
        <div className="card table-list-card-grid">
          <div className="card-body" style={{ maxWidth: "100%", marginRight: "20px" }}>
            <div
              className="container mt-3"
              style={{ maxWidth: "100%", marginLeft: "20px", marginRight: "40px" }}
            >
              <div className="row d-flex flex-wrap align-items-start justify-content-between">
                <div className="col-12 col-md-3 text-start">
                  <label className="form-label">EPF No</label>
                  <Input
                    value={filters.epf_number}
                    onChange={(e) => handleFilterChange("epf_number", e.target.value)}
                    className="form-control"
                    style={{ width: "100%", height: "38px" }}
                  />
                </div>
                <div className="col-12 col-md-2 mb-3 text-start">
                  <label className="form-label">Branch</label>
                  <Select
                    value={filters.branch}
                    options={branchOptions}
                    className="select"
                    placeholder="Select Branch"
                    onChange={(value) => handleFilterChange("branch", value)}
                    allowClear
                    style={{ width: "100%", height: "38px" }}
                  />
                </div>
                <div className="col-12 col-md-2 mb-3 text-start">
                  <label className="form-label">Center</label>
                  <Select
                    value={filters.center}
                    options={centerOptions}
                    className="select"
                    placeholder="Select Center"
                    onChange={(value) => handleFilterChange("center", value)}
                    allowClear
                    style={{ width: "100%", height: "38px" }}
                  />
                </div>
         {activeTab === "birthdays" ? (
  <>
    <div className="col-12 col-md-2 mb-3 text-start">
      <label className="form-label">Month</label>
      <Select
        value={filters.month}
        onChange={(value) => handleFilterChange("month", value)}
        options={monthOptions}
        placeholder="Select Month"
        style={{ width: "100%", height: "38px" }}
      />
    </div>
    <div className="col-12 col-md-2 mb-3 text-start">
      <label className="form-label">Year</label>
      <Select
        value={filters.year}
        onChange={(value) => handleFilterChange("year", value)} // Corrected
        options={yearOptions}
        placeholder="Select Year"
        style={{ width: "100%", height: "38px" }}
      />
    </div>
  </>
) : (
  <div className="col-12 col-md-4 mb-3 text-start">
    <label className="form-label">Date Range</label>
    <RangePicker
      onChange={handleDateRangeChange}
      format="YYYY-MM-DD"
      style={{ width: "100%", height: "38px" }}
    />
  </div>
)}
                <div className="col-12 col-md-1 mb-3 text-start">
                  <label className="form-label">&nbsp;</label>
                  <button
                    disabled={loading}
                    className="btn btn-submit"
                    onClick={() => fetchReportData(activeTab)}
                    style={{ height: "38px", width: "100%" }}
                  >
                    {loading ? "Searching..." : "Search"}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className="card table-list-card-grid">
          <div className="card-body pb-0">
            <Tabs
              activeKey={activeTab}
              onChange={(key) => {
                setActiveTab(key);
                setPagination((prev) => ({ ...prev, current: 1 }));
              }}
            >
              <TabPane tab="Birthdays" key="birthdays">
                <Table
                  loading={loading}
                  columns={columns.birthdays}
                  dataSource={dataSource.birthdays}
                  rowKey={(record) => record.id}
                  className="custom-ant-table-grid"
                  pagination={{
                    current: pagination.current,
                    total: pagination.total,
                    pageSize: pagination.pageSize,
                    pageSizeOptions: ["5", "10", "20", "50"],
                    showSizeChanger: true,
                    showQuickJumper: true,
                    showTotal: (total) => `Total ${total} items`,
                    onShowSizeChange: (current, size) => {
                      setTempPageSize(size);
                      setPageSizeModalVisible(true);
                    },
                  }}
                  onChange={handleTableChange}
                />
              </TabPane>
              <TabPane tab="New Appointments" key="newAppointments">
                <Table
                  loading={loading}
                  columns={columns.newAppointments}
                  dataSource={dataSource.newAppointments}
                  rowKey={(record) => record.id}
                  className="custom-ant-table-grid"
                  pagination={{
                    current: pagination.current,
                    total: pagination.total,
                    pageSize: pagination.pageSize,
                    pageSizeOptions: ["5", "10", "20", "50"],
                    showSizeChanger: true,
                    showQuickJumper: true,
                    showTotal: (total) => `Total ${total} items`,
                    onShowSizeChange: (current, size) => {
                      setTempPageSize(size);
                      setPageSizeModalVisible(true);
                    },
                  }}
                  onChange={handleTableChange}
                />
              </TabPane>
              <TabPane tab="Resignations" key="resignations">
                <Table
                  loading={loading}
                  columns={columns.resignations}
                  dataSource={dataSource.resignations}
                  rowKey={(record) => record.id}
                  className="custom-ant-table-grid"
                  pagination={{
                    current: pagination.current,
                    total: pagination.total,
                    pageSize: pagination.pageSize,
                    pageSizeOptions: ["5", "10", "20", "50"],
                    showSizeChanger: true,
                    showQuickJumper: true,
                    showTotal: (total) => `Total ${total} items`,
                    onShowSizeChange: (current, size) => {
                      setTempPageSize(size);
                      setPageSizeModalVisible(true);
                    },
                  }}
                  onChange={handleTableChange}
                />
              </TabPane>
              <TabPane tab="Maternity Leaves" key="maternityLeaves">
                <Table
                  loading={loading}
                  columns={columns.maternityLeaves}
                  dataSource={dataSource.maternityLeaves}
                  rowKey={(record) => record.id}
                  className="custom-ant-table-grid"
                  pagination={{
                    current: pagination.current,
                    total: pagination.total,
                    pageSize: pagination.pageSize,
                    pageSizeOptions: ["5", "10", "20", "50"],
                    showSizeChanger: true,
                    showQuickJumper: true,
                    showTotal: (total) => `Total ${total} items`,
                    onShowSizeChange: (current, size) => {
                      setTempPageSize(size);
                      setPageSizeModalVisible(true);
                    },
                  }}
                  onChange={handleTableChange}
                />
              </TabPane>
            </Tabs>
          </div>
        </div>
      </div>
      <Modal
        title="Change Page Size"
        open={pageSizeModalVisible}
        onOk={handlePageSizeChange}
        onCancel={() => setPageSizeModalVisible(false)}
        footer={[
          <button
            key="cancel"
            className="btn btn-cancel"
            onClick={() => setPageSizeModalVisible(false)}
          >
            Cancel
          </button>,
          <button
            key="submit"
            className="btn btn-submit"
            onClick={handlePageSizeChange}
          >
            Apply
          </button>,
        ]}
      >
        <div className="form-group">
          <label>Items per page:</label>
          <Select
            style={{ width: "100%" }}
            value={tempPageSize}
            onChange={(value) => setTempPageSize(value)}
          >
            {[5, 10, 20, 50, 100].map((size) => (
              <Option key={size} value={size}>
                {size}
              </Option>
            ))}
          </Select>
        </div>
      </Modal>
    </div>
  );
};

export default EmployeeReports;